import { useState, type FormEvent } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Scales, Eye, EyeSlash, CircleNotch, CheckCircle, EnvelopeSimple } from '@phosphor-icons/react';
import { activateAccount } from '@/lib/onboardingApi';
import { api, getApiErrorMessage } from '@/lib/api';
import { isValidEmail } from '@/lib/validators';
import { useAuthStore } from '@/features/auth/store';
import { routeForMember } from '@/features/auth/hooks';
import type { MemberType } from '@/features/auth/types';
import { MIN_PASSWORD_LENGTH } from '@/lib/passwordPolicy';

function passwordStrength(pw: string): { level: number; label: string; color: string } {
  if (pw.length < MIN_PASSWORD_LENGTH) return { level: 0, label: 'Too short', color: 'bg-red-400' };
  let score = 0;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { level: 1, label: 'Weak', color: 'bg-orange-400' };
  if (score === 2) return { level: 2, label: 'Fair', color: 'bg-yellow-400' };
  if (score === 3) return { level: 3, label: 'Good', color: 'bg-emerald-400' };
  return { level: 4, label: 'Strong', color: 'bg-emerald-600' };
}

export default function AccountActivationPage() {
  const { inviteToken } = useParams<{ inviteToken: string }>();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  // Self-serve recovery: when the link is expired/invalid, let the barrister
  // request a fresh activation email instead of dead-ending at "contact support".
  // Wires to the existing POST /auth/resend-activation (rotates the token).
  const [showResend, setShowResend] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendBusy, setResendBusy] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [resendError, setResendError] = useState('');

  const strength = passwordStrength(password);

  async function handleResend(e: FormEvent) {
    e.preventDefault();
    setResendError('');
    if (!isValidEmail(resendEmail)) { setResendError('Enter a valid email address.'); return; }
    setResendBusy(true);
    try {
      await api.post('/auth/resend-activation', { email: resendEmail.trim().toLowerCase() });
      setResendSent(true);
    } catch (err) {
      setResendError(getApiErrorMessage(err, 'Could not send a new link. Please try again shortly.'));
    } finally {
      setResendBusy(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!inviteToken) { setError('Invalid activation link.'); return; }
    if (password.length < MIN_PASSWORD_LENGTH) { setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }

    setLoading(true);
    try {
      // The activation endpoint sets the password, marks the user active,
      // and returns a full session (access token + user + chambers + permissions).
      // The httpOnly refresh cookie is set by the server on the same response.
      const session = await activateAccount({ inviteToken, password });

      // Store the session — same shape as POST /auth/login.
      setSession({
        accessToken: session.accessToken,
        user: {
          id: session.user.id,
          email: session.user.email,
          firstName: session.user.firstName,
          lastName: session.user.lastName,
          memberType: session.user.memberType as MemberType,
          avatarUrl: session.user.avatarUrl,
          chambersId: session.user.chambersId,
          barristerId: session.user.barristerId,
        },
        chambers: session.chambers,
        permissions: session.permissions,
      });

      setDone(true);

      // Brief delay so the success screen is visible, then redirect.
      // Barristers AND chambers admins go through the first-run experience (Welcome → First Value →
      // First Success) instead of landing cold on an empty dashboard — the admin arc is your-plan →
      // set up your team. Clerks/staff go straight to their workspace.
      const mt = session.user.memberType as MemberType;
      const isBarrister = mt === 'silk' || mt === 'junior' || mt === 'pupil' || mt === 'door_tenant';
      const showWelcome = isBarrister || mt === 'admin';
      setTimeout(
        () => navigate(showWelcome ? '/welcome' : routeForMember(mt), { replace: true }),
        1200,
      );
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      setError(msg ?? 'Could not activate your account. The link may have expired.');
      // Expired/invalid link is exactly when the barrister needs a fresh one — offer it.
      setShowResend(true);
      setResendEmail((prev) => prev || '');
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f8f7f4]">
        <div className="text-center">
          <div className="mb-4 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
              <CheckCircle className="h-8 w-8 text-emerald-600 dark:text-emerald-400" weight="fill" />
            </div>
          </div>
          <h1 className="text-xl font-semibold text-[#1a1915]">Account activated</h1>
          <p className="mt-2 text-sm text-[#6b6961]">Signing you in…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      <header className="border-b border-[#e5e3de] bg-white px-8 py-4">
        <div className="mx-auto max-w-6xl">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded bg-[#1a2f4e]">
              <Scales className="h-4 w-4 text-white" weight="duotone" />
            </div>
            <span className="text-sm font-semibold tracking-tight text-[#1a1915]">Jurivoxa</span>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-md px-6 py-16">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-[#1a1915]">Activate your account</h1>
          <p className="mt-1 text-sm text-[#6b6961]">Set a password to access your Jurivoxa workspace.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 rounded-lg border border-[#e5e3de] bg-white p-8">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-[#3d3b35]">New password *</label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                className="w-full rounded border border-[#d4d0c8] bg-[#faf9f6] py-2.5 pl-3 pr-10 text-sm text-[#1a1915] focus:border-[#1a2f4e] focus:outline-none"
                required
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9c9990]"
              >
                {showPw ? <EyeSlash className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {password && (
              <div className="mt-2">
                <div className="flex gap-1">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className={`h-1 flex-1 rounded-full transition-colors ${
                        i <= strength.level ? strength.color : 'bg-[#e5e3de]'
                      }`}
                    />
                  ))}
                </div>
                <p className="mt-1 text-xs text-[#9c9990]">{strength.label}</p>
              </div>
            )}
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-[#3d3b35]">Confirm password *</label>
            <input
              type={showPw ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Repeat your password"
              className="w-full rounded border border-[#d4d0c8] bg-[#faf9f6] px-3 py-2.5 text-sm text-[#1a1915] focus:border-[#1a2f4e] focus:outline-none"
              required
            />
          </div>

          {error && (
            <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700 dark:text-red-300">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded bg-[#1a2f4e] py-2.5 text-sm font-medium text-white hover:bg-[#243d61] disabled:opacity-60"
          >
            {loading ? (
              <CircleNotch className="h-4 w-4 animate-spin" />
            ) : 'Activate and sign in'}
          </button>
        </form>

        {/* Self-serve recovery — appears when the link is expired/invalid. */}
        {showResend && (
          <div className="mt-4 rounded-lg border border-[#e5e3de] bg-white p-6">
            {resendSent ? (
              <div className="flex items-start gap-3">
                <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600 dark:text-emerald-400" weight="fill" />
                <p className="text-sm text-[#3d3b35]">
                  If an unactivated account exists for that email, a new activation link has been
                  sent. Check your inbox — and your spam folder if it doesn’t arrive within a few
                  minutes. The new link is valid for 7 days.
                </p>
              </div>
            ) : (
              <>
                <div className="mb-1 flex items-center gap-2 text-sm font-medium text-[#1a1915]">
                  <EnvelopeSimple className="h-4 w-4" />
                  Need a new activation link?
                </div>
                <p className="mb-3 text-xs text-[#6b6961]">
                  Enter your email and we’ll send a fresh link. The previous link will stop working.
                </p>
                <form onSubmit={handleResend} className="space-y-3">
                  <input
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="you@chambers.co.uk"
                    className="w-full rounded border border-[#d4d0c8] bg-[#faf9f6] px-3 py-2.5 text-sm text-[#1a1915] focus:border-[#1a2f4e] focus:outline-none"
                    required
                  />
                  {resendError && <p className="text-sm text-red-700 dark:text-red-300">{resendError}</p>}
                  <button
                    type="submit"
                    disabled={resendBusy}
                    className="flex w-full items-center justify-center gap-2 rounded border border-[#1a2f4e] py-2.5 text-sm font-medium text-[#1a2f4e] hover:bg-[#1a2f4e]/5 disabled:opacity-60"
                  >
                    {resendBusy ? <CircleNotch className="h-4 w-4 animate-spin" /> : 'Resend activation link'}
                  </button>
                </form>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
